import pptxgen from "pptxgenjs";

import type {GuideComponents} from "@khan/guide-core";

import {shapes, slideH, slideW} from "./constants.ts";
import {
    addEndSlideContent,
    addGeneralSlideContent,
    addQuestionContent,
    addYoutubeContent,
    createExitTicketSlide,
    createTitleSlide,
} from "./slides.ts";

let shapeCounter = 0;

/**
 * createPPTxFromSlides
 * Uses a JSON representation of slides to create a PPTx file
 */
export async function createPPTxFromSlides(
    slides: GuideComponents["CreateSlides"]["output"]["slides"],
): Promise<Blob> {
    const pres = new pptxgen();
    const slide00 = pres.addSlide();

    createTitleSlide(slide00, slides);

    //
    //  Iterate through the slides
    //
    const numSlides = slides.length;
    for (let i = 1; i < numSlides; i++) {
        const slideData = slides[i];

        const newSlide = pres.addSlide();
        if (
            typeof slideData.title === "string" &&
            slideData.title.toLowerCase().trim() === "exit ticket"
        ) {
            slideData.template = "EXIT_TICKET"; //  hack: prompting wasn't guaranteeing the template type
        }
        if (i < numSlides - 1 && slideData.template !== "EXIT_TICKET") {
            addShapes(newSlide);
        }

        switch (slideData.template) {
            case "EXIT_TICKET":
                createExitTicketSlide(newSlide, slideData);
                break;

            case "QUESTION": //  similar to exit ticket, but assuming there will differentiators
                addQuestionContent(newSlide, slideData);
                break;

            case "YOUTUBE":
                addYoutubeContent(newSlide, slideData, pres);
                break;

            case "END":
                addEndSlideContent(newSlide, pres);
                break;

            default:
                addGeneralSlideContent(newSlide, slideData);
                break;
        }
    }

    // Get the presentation as an ArrayBuffer
    const pptxArrayBuffer = await pres.write({outputType: "arraybuffer"});

    // Convert the ArrayBuffer to a Blob
    const blob = new Blob([pptxArrayBuffer], {
        type: "application/vnd.openxmlformats-officedocument.presentationml.presentation,",
    });

    return blob;
}

const addShapes = (slide: pptxgen.Slide) => {
    const numShapes = Math.ceil(3 * Math.random());
    let x;
    let y;
    for (let i = 0; i < numShapes; i++) {
        const edge = Math.round(3 * Math.random()); //  side starts at top (0) and goes clockwise
        const size = 2 * (Math.random() + 1);
        const offset = size * (Math.random() * 0.2 - 0.5);
        switch (edge) {
            //  top
            case 0:
                x = Math.random() * slideW;
                y = offset;
                break;
            //  right
            case 1:
                x = slideW + offset;
                y = Math.random() * slideH;
                break;
            //  bottom
            case 2:
                x = Math.random() * slideW;
                y = slideH + offset;
                break;
            //  left
            case 3:
                x = offset;
                y = Math.random() * slideH;
                break;
            default:
                break;
        }
        slide.addImage({
            path: shapes[shapeCounter++ % shapes.length],
            w: size,
            h: size,
            x: x,
            y: y,
        });
    }
};
